import {
  BoxType,
  GroupPeopleType,
  preserveClippingSourceMasks,
  type CustomGroupTemplateBox,
  type CustomTemplate,
} from '@lite-workspace/dto-fotoclass-templates';
import { resolveTextWraps } from './text-wrap-zones';
import { TemplateBoxAdapter } from './template-box-adapter';
import type { TemplateAdapterContext, TemplateBoxUnion } from './template-box-adapter';
import type { RenderBox, RenderGroupBox } from './render-box';

function coverBackgroundRect(
  canvasWidth: number,
  canvasHeight: number,
): { x: number; y: number; width: number; height: number; rotation: number } {
  return { x: 0, y: 0, width: canvasWidth, height: canvasHeight, rotation: 0 };
}

function expandCoverBackgroundRect(
  root: RenderGroupBox,
  canvasWidth: number,
  canvasHeight: number,
  isFullSize: boolean,
  placedIds: ReadonlySet<string>,
): RenderGroupBox {
  if (!isFullSize || !root.children?.length) return root;
  return {
    ...root,
    children: root.children.map((child: RenderBox) =>
      child.type === 'background' && !placedIds.has(child.id)
        ? { ...child, rect: coverBackgroundRect(canvasWidth, canvasHeight) }
        : child,
    ),
  };
}

/**
 * Auto-fit owns the background until someone actually places it; from then on its own rect is what screen and
 * print draw. Merely unlocking a layer — done to delete it, restyle it, or as part of a multi-select — is not
 * a placement and must not change what gets printed.
 */
function designerPlacedBackgroundIds(model: CustomTemplate): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const box of model.boxes ?? []) {
    if (box.type === BoxType.BACKGROUND && box.params?.placed) ids.add(box.id);
  }

  return ids;
}

/**
 * The design spread's boxes adapted to the render root group — the single home of the full-bleed rule
 * so the browser WASM render and the server preview render never diverge on how a `full` background expands.
 */
export function buildDesignRenderRoot(model: CustomTemplate): RenderGroupBox | null {
  const templateWidth = model.originalWidth || 0;
  const templateHeight = model.originalHeight || 0;
  if (templateWidth <= 0 || templateHeight <= 0) return null;

  const adapterContext: TemplateAdapterContext = {
    useRuntimeSO: true,
    smartObjectSources: model.smartObjectSources,
    adjustmentResources: model.adjustmentResources,
  };

  const rootGroupBox: CustomGroupTemplateBox = {
    type: BoxType.GROUP,
    id: 'root',
    rect: [0, 0, templateWidth, templateHeight],
    rotation: 0,
    params: { personId: null, peopleType: GroupPeopleType.STUDENT },
    // Hidden layers are not composited by Photoshop; the render drops them while the template keeps them.
    boxes: preserveClippingSourceMasks(model.boxes ?? [], model.originalLayerTree).filter(b => b.visible !== false),
  };

  const root = TemplateBoxAdapter.toRenderBox(rootGroupBox as unknown as TemplateBoxUnion, adapterContext) as RenderGroupBox;
  return expandCoverBackgroundRect(
    // The design is one page: its top-level objects wrap its texts.
    { ...root, children: resolveTextWraps(root.children) },
    templateWidth,
    templateHeight,
    model.type === 'full',
    designerPlacedBackgroundIds(model),
  );
}

/**
 * Flat sibling RenderBoxes for the Rust preview envelope (`CollageV2.boxes`), mirroring the flat shape
 * `print-wire.ts` produces per photobook collage. Empty when the model has no usable dimensions.
 */
export function toDesignPreviewBoxes(model: CustomTemplate): RenderBox[] {
  return buildDesignRenderRoot(model)?.children ?? [];
}

/**
 * One design spread as a Rust preview collage: the flat boxes plus the adjustment registry the renderer resolves
 * LUT/profile ids from. The registry rides once on `originalCustomTemplate`, as on a book collage, never per box.
 */
export function toDesignPreviewCollage(model: CustomTemplate): {
  boxes: RenderBox[];
  originalCustomTemplate?: Pick<CustomTemplate, 'adjustmentResources'>;
} {
  const boxes = toDesignPreviewBoxes(model);
  return model.adjustmentResources?.length
    ? { boxes, originalCustomTemplate: { adjustmentResources: model.adjustmentResources } }
    : { boxes };
}
